import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { PageHeader, Content } from "@/components/PageChrome";
import { TablePagination } from "@/components/TablePagination";
import { Shield, User, Eye, Trash2, Edit2, LogIn, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
  "admin.view_user": { label: "Viewed user", icon: Eye, tone: "text-blue-700 bg-blue-50 border-blue-200" },
  "admin.update_role": { label: "Changed role", icon: Shield, tone: "text-purple-700 bg-purple-50 border-purple-200" },
  "admin.update_user": { label: "Updated user", icon: Edit2, tone: "text-amber-700 bg-amber-50 border-amber-200" },
  "admin.delete_user": { label: "Deleted user", icon: Trash2, tone: "text-red-700 bg-red-50 border-red-200" },
  "consent.update": { label: "Consent change", icon: User, tone: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "auth.login": { label: "Signed in", icon: LogIn, tone: "text-gray-700 bg-gray-100 border-gray-200" },
};

/**
 * Coerce anything to something React can render.
 *
 * A log viewer reads whatever is in the collection, including rows written by
 * an older build or hand-edited in the shell. Rendering a raw value is what
 * produced "Objects are not valid as a React child" and took down the whole
 * page, so every field goes through here: null/undefined become a dash,
 * objects and arrays are JSON-encoded, and dates are formatted.
 */
function text(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toLocaleString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Look up presentation metadata for an action name.
 *
 * `hasOwnProperty` matters: a bare `ACTION_META[key]` would return inherited
 * members of Object.prototype ("constructor", "toString", "__proto__") for
 * those action names, yielding a meta object with no `label`/`icon`.
 */
function metaFor(action) {
  const key = text(action, "");
  if (Object.prototype.hasOwnProperty.call(ACTION_META, key)) return ACTION_META[key];
  return { label: key || "Unknown action", icon: Shield, tone: "text-gray-700 bg-gray-100 border-gray-200" };
}

function humanTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return text(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function DetailChip({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 border border-[var(--border)] rounded-full bg-gray-50 max-w-full">
      <span className="text-gray-500 shrink-0">{text(label, "")}</span>
      <span className="text-[var(--fg)] truncate">{text(value, "—")}</span>
    </span>
  );
}

/** Render one detail value as a single line of text, never as a bare object. */
function detailText(v) {
  if (v && typeof v === "object" && !Array.isArray(v) && "from" in v && "to" in v) {
    return `${text(v.from)} → ${text(v.to)}`;
  }
  return text(v, "");
}

function renderDetails(details) {
  if (!details || typeof details !== "object") return null;
  const pairs = Object.entries(details);
  if (pairs.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {pairs.map(([k, v]) => (
        <DetailChip key={k} label={k} value={detailText(v)} />
      ))}
    </div>
  );
}

const PAGE_SIZE = 25;

const FILTERS = [
  { id: "", label: "All" },
  { id: "admin.view_user", label: "Views" },
  { id: "admin.update", label: "Updates" },
  { id: "admin.delete", label: "Deletes" },
  { id: "consent.", label: "Consent" },
];

export default function AdminAudit() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const load = useCallback(async (actionPrefix, skip, size) => {
    setLoading(true);
    try {
      const params = { limit: size, skip: skip || 0 };
      if (actionPrefix) params.action = actionPrefix;
      const { data } = await api.get("/admin/audit", { params });
      // The endpoint returns {entries, total}; tolerate a bare array from an
      // older server so the page never crashes on a shape mismatch.
      if (Array.isArray(data)) {
        setEntries(data);
        setTotal(data.length);
      } else {
        const rows = Array.isArray(data?.entries) ? data.entries : [];
        setEntries(rows);
        setTotal(Number.isFinite(data?.total) ? data.total : rows.length);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter, page * pageSize, pageSize); }, [filter, page, pageSize, load]);

  const changeFilter = (id) => { setFilter(id); setPage(0); };
  const changePageSize = (n) => { setPageSize(n); setPage(0); };

  const exportCsv = () => {
    // Full-page navigation is fine here: this is a browser download, not app
    // state, and the export endpoint is session-cookie authenticated.
    // API_BASE is required here: window.open bypasses the axios client, so a
    // bare "/admin/audit/export" would 404 against the SPA dev server.
    const q = new URLSearchParams({ format: "csv", limit: "1000" });
    if (filter) q.set("action", filter);
    window.open(`${API_BASE}/admin/audit/export?${q.toString()}`, "_blank");
  };

  const pageCount = Math.ceil(total / pageSize);

  return (
    <>
      <PageHeader
        overline="Compliance"
        title="Audit Log"
        description="Immutable record of every administrative action taken on user data. Used for GDPR-style audits and incident reviews."
        testid="page-audit"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-white inline-flex items-center gap-2" data-testid="btn-export-audit">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={() => load(filter, page * pageSize, pageSize)} className="text-sm px-4 h-10 border border-[var(--border)] rounded-md hover:bg-white inline-flex items-center gap-2" data-testid="btn-refresh-audit">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        }
      />
      <Content>
        <div className="mb-6 flex items-center gap-2 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id || "all"}
              onClick={() => changeFilter(f.id)}
              className={`text-xs px-3 h-8 border rounded-full ${filter === f.id ? "bg-[var(--fg)] text-white border-[var(--fg)]" : "border-[var(--border)] hover:bg-white"}`}
              data-testid={`audit-filter-${f.id || "all"}`}
            >
              {f.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-gray-500 num" data-testid="audit-count">
            {total} entr{total === 1 ? "y" : "ies"}
          </div>
        </div>

        {loading && entries.length === 0 ? (
          <div className="card-flat p-10 text-sm text-gray-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="card-flat p-12 text-center text-sm text-gray-500" data-testid="audit-empty">
            {total === 0 && page > 0
              ? "That page is past the end — go back a page."
              : "No audit entries yet for this filter."}
          </div>
        ) : (
          <>
            <div className="space-y-3" data-testid="audit-timeline">
              {entries.map((e, i) => {
                const meta = metaFor(e.action);
                const Icon = meta.icon;
                const key = text(e.id, `audit-${page}-${i}`);
                return (
                  <div key={key} className="card-flat p-4 flex items-start gap-4" data-testid={`audit-${key}`}>
                    <div className={`w-9 h-9 rounded-full grid place-items-center border ${meta.tone} shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="text-sm">
                            <span className="font-semibold">{text(e.actor_email || e.actor_id)}</span>
                            <span className="text-gray-500"> · {text(meta.label)}</span>
                            {e.target_email ? (
                              <>
                                <span className="text-gray-500"> · </span>
                                <span className="font-mono text-[13px] text-[var(--fg)]">{text(e.target_email)}</span>
                              </>
                            ) : null}
                          </div>
                          <div className="text-[11px] text-gray-500 mt-0.5 font-mono">
                            {humanTime(e.created_at)}
                            {e.ip ? <span className="ml-3">· {text(e.ip)}</span> : null}
                          </div>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wider">
                          {text(e.action, "unknown")}
                        </span>
                      </div>
                      {renderDetails(e.details)}
                    </div>
                  </div>
                );
              })}
            </div>

            <TablePagination
              page={page}
              pageCount={pageCount}
              total={total}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={changePageSize}
              idPrefix="audit"
              noun="entry"
              nounPlural="entries"
            />
          </>
        )}
      </Content>
    </>
  );
}